import "./LanguageSwitcher.css";

/**
 * Language selector for the resident conversation.
 *
 * Deliberately NOT a blocking language gate before the conversation: residents
 * arrive from a referral link, often low-motivation, and a selection wall adds
 * friction against the "hand is always held" principle. Instead English is the
 * default and this sits persistently in the header, so anyone who needs another
 * language can switch in one tap, at any point.
 *
 * Switching is forward-only — the server re-renders the current question in the
 * new language; earlier turns stay as they were sent. Each language is labelled
 * in its OWN script, so a Punjabi speaker can find it without reading English.
 */

/** Endonyms — a language is named in the language itself, never translated. */
const LABELS: Record<string, string> = {
  en: "English",
  pa: "ਪੰਜਾਬੀ",
  gu: "ગુજરાતી",
  ur: "اردو",
};

export function LanguageSwitcher({
  languages,
  current,
  onChange,
  disabled = false,
}: {
  /** Languages the deployment can serve, from GET /languages. */
  languages: string[];
  current: string;
  onChange: (lang: string) => void;
  disabled?: boolean;
}) {
  // Nothing to choose between — don't show a control with one option.
  if (!languages || languages.length < 2) return null;

  return (
    <div className="lang-switcher">
      <label className="lang-switcher__label" htmlFor="lang-select">
        {/* Globe: understood without reading, for residents with low English. */}
        <span aria-hidden="true">🌐</span>
        <span className="sr-only">Choose your language</span>
      </label>
      <select
        id="lang-select"
        className="lang-switcher__select"
        value={current}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
      >
        {languages.map((code) => (
          <option key={code} value={code}>
            {LABELS[code] ?? code}
          </option>
        ))}
      </select>
    </div>
  );
}
